import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Confirm from '../../../components/Confirm';
import type { LineAccount } from '../api/queries';
import { useLineBinding } from '../hooks/useLineBinding';

// Gocochain Support LINE official account "add friend" link (basic ID @939czyiq).
// Override per environment with VITE_LINE_ADD_FRIEND_URL if the OA changes.
const LINE_OA_ADD_FRIEND_URL = import.meta.env.VITE_LINE_ADD_FRIEND_URL || 'https://line.me/R/ti/p/%40010cwdan';

// "Connect LINE" section for the Settings page. A user can link *multiple* LINE
// accounts: we render the list of linked accounts (each removable) and drive the
// one-time-code handshake to add another.
function LineConnectSection() {
    const { t } = useTranslation(undefined, { keyPrefix: 'Information.line' });
    const errorText = t('error') || 'Something went wrong. Please try again.';

    const { accounts, loading, connecting, code, expired, busy, failed, ...line } = useLineBinding();
    const [copied, setCopied] = useState(false);
    const [unlinkTarget, setUnlinkTarget] = useState<LineAccount | null>(null);
    const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(
        () => () => {
            if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
        },
        []
    );

    const requestCode = () => {
        setCopied(false);
        line.requestCode();
    };

    // Back out of the add-account flow without minting/keeping a code.
    const cancelConnect = () => {
        setCopied(false);
        line.cancel();
    };

    const handleCopy = async () => {
        if (!code) return;
        try {
            await navigator.clipboard.writeText(code);
            setCopied(true);
            if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
            copyTimerRef.current = setTimeout(() => setCopied(false), 1500);
        } catch (err) {
            console.error('Clipboard write failed', err);
        }
    };

    const handleUnlinkConfirmed = () => {
        const target = unlinkTarget;
        setUnlinkTarget(null);
        if (target) line.unlink(target.line_user_id);
    };

    const greenButton =
        'flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#06C755] text-base font-semibold text-white shadow-md transition hover:bg-[#05A648] disabled:cursor-not-allowed disabled:opacity-60 md:h-11 md:w-[400px] md:rounded-lg md:shadow-none';
    const outlineButton =
        'flex h-12 w-full items-center justify-center gap-2 rounded-full border border-slate-300 bg-transparent text-base font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60 md:h-11 md:w-[400px] md:rounded-lg';

    const renderAccount = (account: LineAccount) => {
        const name = account.display_name || t('lineUser') || 'LINE user';
        return (
            <div key={account.line_user_id} className='flex items-center gap-4 md:w-[400px]'>
                {account.picture_url ? (
                    <img
                        src={account.picture_url}
                        alt={name}
                        className='h-14 w-14 shrink-0 rounded-full object-cover'
                    />
                ) : (
                    <div className='flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-[#06C755] text-xl font-semibold text-white'>
                        {name.charAt(0).toUpperCase()}
                    </div>
                )}
                <div className='min-w-0 flex-1'>
                    <p className='truncate text-base font-semibold text-slate-900 font-["Roboto"]'>{name}</p>
                    <p className='mt-0.5 text-sm text-[#06C755] font-["Roboto"]'>{t('connected') || 'Connected'}</p>
                </div>
                <button
                    type='button'
                    onClick={() => setUnlinkTarget(account)}
                    disabled={busy}
                    className='shrink-0 rounded-full border border-slate-300 px-4 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60'
                >
                    {t('unlink') || 'Unlink'}
                </button>
            </div>
        );
    };

    // The one-time-code handshake panel shown while adding an account.
    const renderCodePanel = () => (
        <div className='space-y-4'>
            <p className='text-sm font-medium text-slate-600 font-["Roboto"]'>{t('instructions')}</p>
            {/* Must stay a real anchor. window.open() is a script-initiated navigation, so iOS
                never matches the line.me universal link and the Gocochain app's WebView just
                loads line.me in place — where LINE's "Open LINE" interstitial fires the line://
                scheme that the WebView cannot handle, leaving the tap dead. A user-activated
                link lets the OS hand the URL straight to the LINE app instead. */}
            <a
                href={LINE_OA_ADD_FRIEND_URL}
                target='_blank'
                rel='noopener noreferrer'
                className='flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#06C755]/10 text-[16px] font-semibold text-[#06C755] transition hover:bg-[#06C755]/20 md:h-11 md:w-[400px]'
            >
                {t('addOa') || 'Add LINE OA'}
            </a>

            <div className='md:w-[400px]'>
                <div
                    className={`relative flex items-center justify-start rounded-2xl border px-5 py-4 ${
                        expired ? 'border-slate-200 bg-slate-100 opacity-60' : 'border-slate-200 bg-slate-50'
                    }`}
                >
                    <span className='font-mono text-2xl font-bold tracking-[0.25em] text-[#1E2B7C] md:text-3xl'>
                        {code}
                    </span>
                    <button
                        type='button'
                        onClick={handleCopy}
                        disabled={expired}
                        className='absolute right-4 top-1/2 -translate-y-1/2 shrink-0 rounded-full border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-50'
                    >
                        {copied ? t('copied') || 'Copied' : t('copy') || 'Copy'}
                    </button>
                    {expired && (
                        <span className='absolute bottom-1.5 left-4 text-[10px] font-medium leading-none text-red-500 font-["Roboto"]'>
                            {t('expired') || 'Code expired'}
                        </span>
                    )}
                </div>
            </div>

            {failed && <p className='text-sm text-red-500 font-["Roboto"]'>{errorText}</p>}

            {expired && (
                <button type='button' onClick={requestCode} disabled={busy} className={greenButton}>
                    {t('generateNew') || 'Generate new code'}
                </button>
            )}

            <button
                type='button'
                onClick={cancelConnect}
                className='inline-flex items-center justify-center rounded-full bg-red-500/10 px-4 py-1.5 text-sm font-medium text-red-500 transition hover:bg-red-500/20'
            >
                {t('cancel') || 'Cancel'}
            </button>
        </div>
    );

    const renderBody = () => {
        if (loading) {
            return <p className='text-sm text-slate-500 font-["Roboto"]'>{t('loading') || 'Loading…'}</p>;
        }

        const hasAccounts = accounts.length > 0;

        return (
            <div className='space-y-5'>
                {hasAccounts && (
                    <div className='space-y-3'>
                        <p className='text-sm font-semibold text-slate-500 font-["Roboto"]'>
                            {t('connectedAccounts') || 'Connected accounts'}
                        </p>
                        {accounts.map(renderAccount)}
                    </div>
                )}

                {connecting ? (
                    renderCodePanel()
                ) : (
                    <div className='space-y-3'>
                        {!hasAccounts && (
                            <p className='text-sm font-medium text-slate-600 font-["Roboto"]'>{t('intro')}</p>
                        )}
                        {failed && <p className='text-sm text-red-500 font-["Roboto"]'>{errorText}</p>}
                        <button
                            type='button'
                            onClick={requestCode}
                            disabled={busy}
                            className={hasAccounts ? outlineButton : greenButton}
                        >
                            {hasAccounts
                                ? t('connectAnother') || 'Connect another account'
                                : t('connect') || 'Connect LINE'}
                        </button>
                    </div>
                )}
            </div>
        );
    };

    return (
        <section className='mt-8 space-y-5 md:mt-8 md:space-y-6 md:border-b md:border-slate-300 md:pb-8'>
            <div>
                <p className='text-[#1E2B7C] text-xl font-semibold font-["Roboto"]'>
                    {t('sectionTitle') || 'LINE Notification'}
                </p>
                <div className='mt-3 h-px w-full bg-slate-200' />
            </div>
            {renderBody()}
            <Confirm
                openConfirm={unlinkTarget !== null}
                setOpenConfirm={(open: boolean) => {
                    if (!open) setUnlinkTarget(null);
                }}
                handleConfirm={handleUnlinkConfirmed}
                records={[]}
                tKey='Information.line.Confirm'
            />
        </section>
    );
}

export default LineConnectSection;
